/**
 * `<playing-card-hand>`: a fanned hand, turned to face the seat it belongs to.
 *
 * Face down it is only a count drawn as card backs. Face up it is the
 * player's own hand, where every card is a toggle button and the whole hand is
 * a single tab stop - a full hand is eleven cards or more, and making each its
 * own tab stop would bury every other control on the page behind them.
 */

import type { Card } from "@ryzun/haf-saloon-game-engine";
import { getCardLabel } from "../cards.js";
import { h, setAttribute, setAttributes } from "../dom.js";
import { emit } from "../events.js";
import type { RunPosition } from "../play.js";
import { getPlayableRuns } from "../play.js";
import type { SeatOrientation } from "../seating.js";
import { BaseElement } from "./base-element.js";
import styles from "./card-hand.css";
import "./playing-card.js";

/** Everything the player's own hand needs to draw itself. */
export interface OwnHand {
    /** Already in display order; the hand never reorders what it is given. */
    readonly cards: readonly Card[];
    readonly selected: readonly string[];
    readonly playable: ReadonlySet<string>;
    /** Cards that may be picked now; empty outside the player's turn. */
    readonly selectable: ReadonlySet<string>;
    readonly label: string;
}

export class CardHand extends BaseElement {
    #orientation: SeatOrientation = "south";

    #count = 0;

    #own: OwnHand | null = null;

    /** Buttons by card id, kept across renders so focus survives an update. */
    readonly #buttons = new Map<string, HTMLButtonElement>();

    #focusId: string | null = null;

    readonly #fan = h("div", { class: "fan" });

    constructor() {
        super(styles);

        this.root.append(this.#fan);
        this.#fan.addEventListener("keydown", (event) => this.#onKeyDown(event));
    }

    get orientation(): SeatOrientation {
        return this.#orientation;
    }

    set orientation(value: SeatOrientation) {
        this.#orientation = value;
        this.invalidate();
    }

    /** How many backs to fan out; ignored once the hand is face up. */
    get count(): number {
        return this.#count;
    }

    set count(value: number) {
        this.#count = value;
        this.invalidate();
    }

    /** The player's own cards, or null for anyone else's hand. */
    get own(): OwnHand | null {
        return this.#own;
    }

    set own(value: OwnHand | null) {
        this.#own = value;
        this.invalidate();
    }

    protected render(): void {
        const own = this.#own;

        setAttributes(this, {
            "face-up": own !== null,
            "orientation": this.#orientation
        });

        if (own === null) {
            this.#renderBacks();
        }
        else {
            this.#renderOwn(own);
        }
    }

    #renderBacks(): void {
        this.#buttons.clear();
        this.style.setProperty("--count", String(this.#count));
        setAttribute(this, "aria-hidden", "true");
        setAttribute(this.#fan, "role", null);
        setAttribute(this.#fan, "aria-label", null);

        this.#fan.replaceChildren(...Array.from({ length: this.#count }, () => document.createElement("playing-card")));
    }

    #renderOwn(own: OwnHand): void {
        const focused = this.root.activeElement;

        this.style.setProperty("--count", String(Math.max(own.cards.length, 1)));
        setAttribute(this, "aria-hidden", null);
        setAttributes(this.#fan, {
            "role": "group",
            "aria-label": `${own.label}, ${own.cards.length} cards`
        });

        const ids = new Set(own.cards.map((card) => card.id));

        for (const id of this.#buttons.keys()) {
            if (!ids.has(id)) {
                this.#buttons.delete(id);
            }
        }

        if (this.#focusId === null || !ids.has(this.#focusId)) {
            this.#focusId = own.cards[0]?.id ?? null;
        }

        const runs = getPlayableRuns(own.cards, own.playable);
        const ordered = own.cards.map((card) => this.#updateButton(card, own, runs.get(card.id) ?? null));

        if (ordered.length === 0) {
            this.#fan.replaceChildren(h("p", { class: "empty" }, "No cards left."));
            return;
        }

        this.#placeInOrder(ordered);

        // Moving a focused node drops its focus, so put it back where it was.
        if (focused instanceof HTMLButtonElement && focused.isConnected && this.root.activeElement !== focused) {
            focused.focus();
        }
    }

    #updateButton(card: Card, own: OwnHand, run: RunPosition | null): HTMLButtonElement {
        let button = this.#buttons.get(card.id);

        if (button === undefined) {
            const face = document.createElement("playing-card");
            face.card = card;

            button = h("button", { "type": "button", "class": "slot", "data-card-id": card.id }, face);
            button.addEventListener("click", () => this.#onClick(card.id));
            button.addEventListener("focus", () => {
                this.#focusId = card.id;
                this.#updateTabStops();
            });

            this.#buttons.set(card.id, button);
        }

        const selected = own.selected.includes(card.id);
        const playable = own.playable.has(card.id);

        setAttributes(button, {
            "tabindex": card.id === this.#focusId ? 0 : -1,
            "aria-pressed": String(selected),
            "aria-disabled": String(!own.selectable.has(card.id)),
            "aria-label": `${getCardLabel(card)}${playable ? ", playable" : ""}`,
            "data-playable": playable,
            "data-run": run
        });

        return button;
    }

    /**
     * Puts the buttons in order with as few moves as possible. Only a button
     * that is out of place is moved, so an untouched card - most likely the one
     * with focus - stays in the document throughout.
     */
    #placeInOrder(ordered: readonly HTMLButtonElement[]): void {
        for (const [index, button] of ordered.entries()) {
            const current = this.#fan.children[index];

            if (current !== button) {
                this.#fan.insertBefore(button, current ?? null);
            }
        }

        while (this.#fan.children.length > ordered.length) {
            this.#fan.lastElementChild?.remove();
        }
    }

    #updateTabStops(): void {
        for (const [id, button] of this.#buttons) {
            button.tabIndex = id === this.#focusId ? 0 : -1;
        }
    }

    #onClick(cardId: string): void {
        if (this.#own?.selectable.has(cardId) === true) {
            emit(this, "card-toggle", { cardId });
        }
    }

    #onKeyDown(event: KeyboardEvent): void {
        const cards = this.#own?.cards ?? [];
        const current = cards.findIndex((card) => card.id === this.#focusId);
        let next: number;

        switch (event.key) {
            case "ArrowRight": {
                next = current + 1;
                break;
            }

            case "ArrowLeft": {
                next = current - 1;
                break;
            }

            case "Home": {
                next = 0;
                break;
            }

            case "End": {
                next = cards.length - 1;
                break;
            }

            default: {
                return;
            }
        }

        event.preventDefault();

        const target = cards[Math.min(Math.max(next, 0), cards.length - 1)];

        if (target !== undefined) {
            this.#buttons.get(target.id)?.focus();
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "playing-card-hand": CardHand;
    }
}

customElements.define("playing-card-hand", CardHand);
